import { useState } from "react";
import { T } from "@/components/T";
import { Link, useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AuthShell, GoogleButton, PasswordField } from "@/pages/Login";
import { Divider } from "@/components/MotifBackground";
import { Gift } from "lucide-react";

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const inputCls = "w-full border border-line bg-parchment px-4 py-2.5 text-ink focus:border-saffron focus:outline-none";

export default function Register() {
  const { user, ready, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = (searchParams.get("ref") || "").trim();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [agreed, setAgreed] = useState(false);

  if (ready && user) return <Navigate to="/account" replace />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!agreed) {
      setError("Please accept the Terms of Service, Privacy Policy, Disclaimer and DPDP Act Notice to continue.");
      return;
    }
    setBusy(true);
    try {
      const data = await register(form.name, form.email, form.password, referralCode);
      if (data.requires_verification) {
        setSentTo(data.email || form.email);
      } else {
        navigate("/onboarding");
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (sentTo) {
    return (
      <AuthShell title="Confirm your email" subtitle="One more step">
        <div className="space-y-5 text-center" data-testid="register-verify-sent">
          <p className="text-4xl text-saffron" aria-hidden="true">ॐ</p>
          <p className="text-sm text-ink-2"><T>We've sent a confirmation link to</T> <strong>{sentTo}</strong><T>. Please open it to activate your account, then sign in.</T></p>
          <p className="text-xs text-ink-3"><T>Didn't get it? Check your spam folder, or request a new link from the sign-in page.</T></p>
          <Link to="/login" className="inline-block text-sm text-saffron link-underline"><T>Go to sign in</T></Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Create account" subtitle="Track your bookings, sadhana points and consultations">
      {referralCode && (
        <div data-testid="register-referral-note" className="mb-5 flex items-start gap-2.5 border border-[var(--gold-muted)] bg-[rgba(176,141,87,0.06)] px-4 py-3 text-sm text-ink-2">
          <Gift className="mt-0.5 h-4 w-4 shrink-0 text-saffron" />
          <span><T>You were invited by a fellow sadhaka — you'll both earn bonus points when you join.</T></span>
        </div>
      )}
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-sm text-ink-2"><T>Name</T></label>
          <input data-testid="register-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="mb-1.5 block text-sm text-ink-2"><T>Email</T></label>
          <input data-testid="register-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="mb-1.5 block text-sm text-ink-2"><T>Password</T></label>
          <PasswordField testId="register-password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} />
        </div>
        {error && <p data-testid="register-error" className="text-sm text-[var(--saffron-dark)]">{error}</p>}
        <label className="flex items-start gap-2.5 text-sm text-ink-2" data-testid="register-consent-label">
          <input
            type="checkbox"
            data-testid="register-consent"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--saffron)]"
          />
          <span>
            <T>I accept the</T>{" "}
            <Link to="/terms" target="_blank" rel="noreferrer" className="text-saffron link-underline"><T>Terms of Service</T></Link>,{" "}
            <Link to="/privacy" target="_blank" rel="noreferrer" className="text-saffron link-underline"><T>Privacy Policy</T></Link>,{" "}
            <Link to="/disclaimer" target="_blank" rel="noreferrer" className="text-saffron link-underline"><T>Disclaimer</T></Link>{" "}
            <T>and the</T>{" "}
            <Link to="/dpdp" target="_blank" rel="noreferrer" className="text-saffron link-underline"><T>DPDP Act Notice</T></Link>.
          </span>
        </label>
        <button data-testid="register-submit" disabled={busy || !agreed} className="w-full bg-[var(--saffron)] py-3 font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
          {busy ? "Creating…" : "Create account"}
        </button>
      </form>
      <div className="my-6"><Divider /></div>
      <GoogleButton />
      <p className="mt-6 text-center text-sm text-ink-3">
        <T>Already have an account?</T> <Link to="/login" className="text-saffron link-underline"><T>Sign in</T></Link>
      </p>
    </AuthShell>
  );
}
